import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];

const SWEET_COLORS = [
  "#5A3A5A",
  "#7B5EA7",
  "#9B7EBD",
  "#C97B8A",
  "#D9A5B3",
  "#C5A059",
  "#8C6A53",
  "#E8D8B0",
];

const Candy = ({ color, wrapped }) =>
  wrapped ? (
    <svg viewBox="0 0 44 22" className="h-full w-full" aria-hidden="true">
      <path d="M10 11 L0 3 V19 Z" fill={color} />
      <rect x="10" y="2" width="24" height="18" rx="9" fill={color} />
      <path d="M34 11 L44 3 V19 Z" fill={color} />
      <rect x="16" y="6" width="12" height="3" rx="1.5" fill="rgba(255,255,255,0.45)" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" className="h-full w-full" aria-hidden="true">
      <circle cx="10" cy="10" r="9" fill={color} />
      <circle cx="7" cy="7" r="2.5" fill="rgba(255,255,255,0.5)" />
    </svg>
  );

export default function Envelope({ opening, onOpen, onOpened }) {
  useEffect(() => {
    if (!opening) return undefined;
    const t = setTimeout(onOpened, 2200);
    return () => clearTimeout(t);
  }, [opening, onOpened]);

  const sweets = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => {
        const angle = (i / 42) * Math.PI * 2 + Math.random() * 0.5;
        const dist = 150 + Math.random() * 240;
        return {
          id: i,
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist * 0.7 - 70,
          fall: 340 + Math.random() * 260,
          rotate: (Math.random() - 0.5) * 540,
          size: 18 + Math.random() * 22,
          color: SWEET_COLORS[i % SWEET_COLORS.length],
          wrapped: i % 2 === 0,
          delay: 0.35 + Math.random() * 0.15,
        };
      }),
    [],
  );

  const smokes = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 280,
        y: (Math.random() - 0.5) * 170,
        size: 130 + Math.random() * 150,
        delay: 0.3 + Math.random() * 0.2,
      })),
    [],
  );

  return (
    <motion.div
      data-testid="envelope-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{
        background:
          "radial-gradient(130% 110% at 50% 18%, #6b5340 0%, #46352a 42%, #241a12 100%)",
      }}
      exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(55% 40% at 50% 44%, rgba(232,216,176,0.20), transparent 72%)",
        }}
      />

      <div className="relative">
        {opening &&
          smokes.map((s) => (
            <motion.div
              key={`smoke-${s.id}`}
              className="pointer-events-none absolute left-1/2 top-1/2 rounded-full"
              style={{
                width: s.size,
                height: s.size,
                marginLeft: -s.size / 2,
                marginTop: -s.size / 2,
                background:
                  "radial-gradient(circle, rgba(250,248,245,0.75), rgba(250,248,245,0) 70%)",
                filter: "blur(6px)",
              }}
              initial={{ x: 0, y: 0, scale: 0.3, opacity: 0 }}
              animate={{ x: s.x, y: s.y, scale: 1.7, opacity: [0, 0.8, 0] }}
              transition={{ delay: s.delay, duration: 1.6, ease: "easeOut" }}
            />
          ))}

        {opening &&
          sweets.map((s) => (
            <motion.div
              key={`sweet-${s.id}`}
              className="pointer-events-none absolute left-1/2 top-1/2"
              style={{
                width: s.size,
                height: s.size,
                marginLeft: -s.size / 2,
                marginTop: -s.size / 2,
              }}
              initial={{ x: 0, y: 0, scale: 0, opacity: 1, rotate: 0 }}
              animate={{
                x: s.x,
                y: [0, s.y, s.y + s.fall],
                scale: [0, 1.15, 1],
                opacity: [1, 1, 0],
                rotate: s.rotate,
              }}
              transition={{
                delay: s.delay,
                duration: 1.7,
                times: [0, 0.45, 1],
                ease: "easeOut",
              }}
            >
              <Candy color={s.color} wrapped={s.wrapped} />
            </motion.div>
          ))}

        <motion.button
          type="button"
          data-testid="envelope-open-button"
          aria-label="Open your invitation"
          onClick={onOpen}
          disabled={opening}
          className="relative block cursor-pointer"
          style={{ width: "min(86vw, 400px)", aspectRatio: "8 / 5" }}
          initial={{ y: 60, opacity: 0, scale: 0.94, rotate: -2 }}
          animate={opening ? "burst" : "enter"}
          variants={{
            enter: {
              y: 0,
              opacity: 1,
              scale: 1,
              rotate: 0,
              transition: { duration: 1, ease: EASE },
            },
            burst: {
              rotate: [0, -2, 2, -1, 0],
              scale: [1, 1.05, 1.18],
              opacity: [1, 1, 0],
              transition: { duration: 0.75, times: [0, 0.5, 1], ease: "easeIn" },
            },
          }}
          whileHover={opening ? {} : { scale: 1.03 }}
          whileTap={opening ? {} : { scale: 0.97 }}
        >
          <motion.div
            className="absolute inset-0"
            animate={opening ? { y: 0 } : { y: [0, -12, 0] }}
            transition={
              opening
                ? { duration: 0.2 }
                : { duration: 1.3, repeat: Infinity, ease: "easeInOut" }
            }
          >
          <motion.span
            data-testid="mail-notification-badge"
            className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#5A3A5A] text-sm font-semibold text-white shadow-lg"
            animate={opening ? { opacity: 0, scale: 0 } : { scale: [1, 1.18, 1] }}
            transition={
              opening
                ? { duration: 0.3 }
                : { duration: 1.3, repeat: Infinity, ease: "easeInOut" }
            }
          >
            1
          </motion.span>
          <div className="absolute inset-0 rounded-xl bg-[#f3eddf] shadow-[0_30px_80px_rgba(0,0,0,0.5)]" />
          <div
            className="absolute inset-0 rounded-xl bg-[#faf6ec]"
            style={{
              clipPath: "polygon(0 0, 50% 46%, 100% 0, 100% 100%, 0 100%)",
            }}
          />
          <motion.div
            className="absolute inset-x-0 top-0 h-[52%] border-b border-[#CBB8D4] bg-[#E9DFEC]"
            style={{
              clipPath: "polygon(0 0, 100% 0, 50% 100%)",
              transformOrigin: "top center",
              transformPerspective: 900,
            }}
            initial={false}
            animate={opening ? { rotateX: -170, opacity: 0 } : { rotateX: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeIn" }}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[42%] flex flex-col items-center justify-center gap-2">
            <span className="font-script text-4xl text-[#5A3A5A] sm:text-5xl">
              You&apos;ve Got Mail
            </span>
            <motion.span
              data-testid="envelope-hint"
              className="text-[10px] uppercase tracking-[0.35em] text-[#8C6A53]"
              animate={opening ? { opacity: 0 } : { opacity: [0.45, 1, 0.45] }}
              transition={
                opening
                  ? { duration: 0.3 }
                  : { duration: 2.2, repeat: Infinity, ease: "easeInOut" }
              }
            >
              Click to open
            </motion.span>
          </div>
          </motion.div>
        </motion.button>
      </div>
    </motion.div>
  );
}
